import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { ImageModule } from '@df/module-image/image.module';
import { BgColorOptions, ColorOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { defaultImagePng } from 'projects/app-default/src/app/icms.config';
import { first } from 'rxjs/operators';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
export namespace NAdventCalendar {
    export const modalId = 'advent-calendar-modal';

    export enum ETileStatus {
        available,
        expired,
        expiredOpened,
        soon,
        opened
    }
    export interface IData extends NICmsContentComponent.IData {
        title: string;
        subtitle?: string;
        description?: string;
        background?: string;
        labelBackground: string;
        labelTextColor: string;
        expiredLabel: string;
        limitedTimeOfferLabel: string;
        tiles: ITile[];
        expiredImage: string;
        expiredTitle: string;
        expiredDescription?: string;
        expiredLink: string;
        expiredButtonText: string;
    }

    export interface ITile {
        mainImage: string;
        afterOpenImage?: string;
        productId?: number;
        imageInPopup?: string;
        popupTitle: string;
        popupDescription: string;
        popupButtonText: string;
        popupLink: string;
        numberColorWhite: string;
        startDate: string;
        endDate: string;
    }

    export interface IExtendedTile extends ITile {
        number: number;
        status: ETileStatus;
        product?: Product<IProductDataV3>;
    }
}

@ICmsComponent<PageComponentAdventCalendarComponent>({
    id: 'advent-calendar',
    name: 'Advent calendar',
    hidePreview: true,
    fields: [
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true
        },
        {
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle'
        },
        {
            key: 'description',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Description'
        },
        <NICmsContentField.IChoiceField>{
            key: 'background',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background',
            previewMode: 'color',
            options: BgColorOptions
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'additional_label',
            title: 'Labels settings'
        },
        <NICmsContentField.IChoiceField>{
            key: 'labelBackground',
            type: NICmsContentField.EFieldType.choice,
            title: 'Labels background',
            previewMode: 'color',
            options: BgColorOptions,
            default: 'bg-col-6',
            required: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'labelTextColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Labels text color',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-w',
            required: true
        },
        {
            key: 'expiredLabel',
            type: NICmsContentField.EFieldType.text,
            title: 'Expired label',
            default: 'Offer Expired',
            description: 'Label have to be short to fit mobile view',
            required: true
        },
        {
            key: 'limitedTimeOfferLabel',
            type: NICmsContentField.EFieldType.text,
            title: 'Limited time offer label',
            default: 'Limited Time offer',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'tiles',
            title: 'Tiles',
            itemName: 'Tile',
            minSize: 12,
            maxSize: 12,
            defaultSize: 1,
            fields: [
                {
                    key: 'mainImage',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Main image',
                    default: defaultImagePng,
                    required: true
                },
                {
                    key: 'afterOpenImage',
                    type: NICmsContentField.EFieldType.image,
                    title: 'After open image',
                    default: defaultImagePng,
                    description: 'Image that will be shown after the tile is opened, if empty main image will be shown'
                },
                <NICmsContentField.IRelationField>{
                    key: 'productId',
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Product',
                    options: {
                        title: 'Product',
                        type: 'product',
                        multiple: false,
                        ordered: true
                    }
                },
                {
                    key: 'imageInPopup',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image in popup',
                    required: (data: NAdventCalendar.ITile) => !data.productId
                },
                {
                    key: 'popupTitle',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Popup title',
                    default: 'Popup title',
                    description: 'Its override for product name',
                    required: (data: NAdventCalendar.ITile) => !data.productId
                },
                {
                    key: 'popupDescription',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Popup description',
                    default: 'Popup description',
                    description: 'Its override for product description',
                    required: (data: NAdventCalendar.ITile) => !data.productId
                },
                {
                    key: 'popupButtonText',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Popup button text',
                    default: 'Popup button',
                    required: true
                },
                {
                    key: 'popupLink',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Popup link',
                    required: (data: NAdventCalendar.ITile) => !data.productId,
                    canShow: (data: NAdventCalendar.ITile) => !data.productId
                },
                <NICmsContentField.IToggleField>{
                    key: 'numberColorWhite',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Number color',
                    labels: { true: 'White', false: 'Black' },
                    default: false,
                    required: true
                },
                {
                    key: 'startDate',
                    type: NICmsContentField.EFieldType.date,
                    title: 'Start date',
                    required: true
                },
                {
                    key: 'endDate',
                    type: NICmsContentField.EFieldType.date,
                    title: 'End date',
                    required: true
                }
            ]
        },
        {
            key: 'expiredImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Expired image',
            default: defaultImagePng,
            required: true
        },
        {
            key: 'expiredTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Expired title',
            required: true
        },
        {
            key: 'expiredDescription',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Expired description'
        },
        {
            key: 'expiredLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Expired link',
            required: true
        },
        {
            key: 'expiredButtonText',
            type: NICmsContentField.EFieldType.text,
            title: 'Expired button text',
            required: true
        }
    ]
})
@Component({
    selector: 'page-component-advent-calendar',
    templateUrl: './advent-calendar.html',
    styleUrls: ['./advent-calendar.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        HrefComponent,
        ICmsClientModule,
        ImageModule,
        NgClass,
        NgClass,
        NgTemplateOutlet,
        ShellComponent,
        SizeClassDirective,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentAdventCalendarComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NAdventCalendar.IData;

    readonly tileStatus = NAdventCalendar.ETileStatus;
    private dfModalService = inject(DfModalService);
    private openedNumbers: number[] = [];
    private productResource = inject(ProductResource);
    protected tiles: NAdventCalendar.IExtendedTile[] = [];

    // call detectChanges to update view on init data
    override canDetach = true;

    // Function to normalize the date to midnight in the local timezone
    private normalizeDateString(date: Date): string {
        return `${date.getFullYear()}-${date.getMonth() < 10 ? '0' : ''}${date.getMonth() + 1}-${date.getDate() < 10 ? '0' : ''}${date.getDate()}`;
    }

    private getTileStatus(tile: NAdventCalendar.ITile, number: number): NAdventCalendar.ETileStatus {
        const now = this.normalizeDateString(new Date());

        const startDate = tile.startDate;

        const endDate = tile.endDate;

        if (now > endDate) {
            return this.openedNumbers.includes(number) ? NAdventCalendar.ETileStatus.expiredOpened : NAdventCalendar.ETileStatus.expired;
        }

        if (this.openedNumbers.includes(number)) {
            return NAdventCalendar.ETileStatus.opened;
        }

        if (now < startDate) {
            return NAdventCalendar.ETileStatus.soon;
        }

        return NAdventCalendar.ETileStatus.available;
    }

    protected getStatusClass(status: NAdventCalendar.ETileStatus): string {
        switch (status) {
            case NAdventCalendar.ETileStatus.available:
                return '_available';
            case NAdventCalendar.ETileStatus.expiredOpened:
            case NAdventCalendar.ETileStatus.expired:
                return '_expired';
            case NAdventCalendar.ETileStatus.soon:
                return '_soon';
            default:
                return '_opened';
        }
    }

    protected getStatusLabel(status: NAdventCalendar.ETileStatus): string | undefined {
        switch (status) {
            case NAdventCalendar.ETileStatus.expiredOpened:
            case NAdventCalendar.ETileStatus.expired:
                return this.component.expiredLabel;
            // case NAdventCalendar.ETileStatus.soon:
            //     return this.component.availableSoonLabel;
            default:
                return;
        }
    }

    protected openTile(tile: NAdventCalendar.IExtendedTile): void {
        if (tile.status === NAdventCalendar.ETileStatus.soon && !this.client.isCms) {
            return;
        }
        this.dfModalService.open(undefined, NAdventCalendar.modalId, undefined, { tile, component: this.component }, modalRef => {
            // After close trigger updateimage original view
            modalRef
                .afterClosed()
                .pipe(first())
                .subscribe(() => {
                    if (tile.status !== NAdventCalendar.ETileStatus.opened) {
                        this.openedNumbers.push(tile.number);
                        this.detectChanges();
                    }
                });
        });
    }

    override detectChanges(): void {
        // update tiles status and product data
        this.tiles =
            this.component?.tiles?.map((tile, number) => ({ ...tile, number: number + 1, status: this.getTileStatus(tile, number + 1) })) ||
            [];
        const productsIdsToLoad = this.tiles
            .map(t => (t.productId && t.productId !== t.product?.id ? t.productId : undefined))
            .filter(id => !!id);
        if (productsIdsToLoad.length) {
            // v3 cause of description
            this.productResource.loadAllIds(productsIdsToLoad as number[], 3, { safe: true }).then(products => {
                this.tiles.forEach(tile => {
                    if (tile.productId) {
                        tile.product = (products as Product<IProductDataV3>[]).find(p => p.id === tile.productId);
                    }
                });
                this.changeDetectorRef.detectChanges();
            });
        }

        super.detectChanges();
    }

    protected get isCalendarExpired(): boolean {
        return this.tiles.every(tile => tile.status === NAdventCalendar.ETileStatus.expired);
    }
}
